import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';

const ForecastTrendChart = ({ data, quarterLabel, quarter, status, target, actualWon, onDrill, config = {} }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    const containerEl = chartRef.current;

    // Create tooltip div if not exists
    if (!tooltipRef.current) {
      tooltipRef.current = d3.select('body')
        .append('div')
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('background', 'rgba(255,255,255,0.95)')
        .style('color', 'black')
        .style('font-family', 'roboto')
        .style('font-size', '1rem')
        .style('border-radius', '1rem')
        .style('padding', '0')
        .style('box-shadow', '2px 2px 4px 1px darkgrey')
        .style('white-space', 'nowrap')
        .style('min-width', '320px')
        .style('opacity', 0.5)
        .style('z-index', 10000000);
    }

    const tooltip = tooltipRef.current;

    const draw = () => {
      const rect = containerEl.getBoundingClientRect();
      const outerWidth = Math.max(320, Math.floor(rect.width || 0));
      const outerHeight = 250;

      // Clear previous SVG only (keep tooltip div)
      d3.select(containerEl).selectAll('svg').remove();

      const margin = { top: 30, right: 30, bottom: 55, left: 60 };
      const width = Math.max(200, outerWidth - margin.left - margin.right);
      const height = Math.max(140, outerHeight - margin.top - margin.bottom);

      const yDecimals = config.yAxisDecimals !== undefined ? Number(config.yAxisDecimals) : 1;
      const yFormat = config.yAxisFormat || 'M';

      const root = d3
        .select(containerEl)
        .append('svg')
        .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('width', '100%')
        .style('height', '100%')
        .style('display', 'block');

      const svg = root
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const validData = data.filter(d => d.won !== null || d.mostLikely !== null);

      const formatWeekEnding = (weekEnding) => {
        if (!weekEnding) return '';
        if (typeof weekEnding === 'string' && weekEnding.match(/^\d{4}-\d{2}-\d{2}/)) {
          return weekEnding.substring(0, 10);
        }
        const date = new Date(weekEnding);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const fmtVal = (v) => {
        if (yFormat === 'K') return (v / 1000).toFixed(yDecimals);
        return (v / 1000000).toFixed(yDecimals);
      };

      const xScale = d3
        .scaleBand()
        .domain(data.map(d => d.weekEnding || d.week))
        .range([0, width])
        .padding(0.2);

      const showActualWon = status === 'elapsed' && (actualWon || 0) > 0;

      const maxValue = Math.max(
        target || 0,
        showActualWon ? actualWon : 0,
        d3.max(data, d => (d.won || 0) + (d.mostLikely || 0) + (d.bestCase || 0)) || 0
      ) * 1.1;

      const yScale = d3
        .scaleLinear()
        .domain([0, maxValue])
        .range([height, 0]);

      // Background
      const bgColor = status === 'elapsed' ? '#f5f5f5' : '#fff';
      svg
        .append('rect')
        .attr('x', -margin.left)
        .attr('y', -margin.top)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('fill', bgColor);

      // X Axis
      svg
        .append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(xScale).tickSize(0).tickFormat(() => ''));

      data.forEach((d) => {
        const weekNum = d.week !== undefined ? d.week : d.weekLabel;
        const xPos = xScale(d.weekEnding || d.week) + xScale.bandwidth() / 2;

        svg.append('text')
          .attr('x', xPos).attr('y', height + 15)
          .attr('text-anchor', 'middle').style('font-size', '9px').style('font-weight', 'bold')
          .text(weekNum);

        const dateStr = formatWeekEnding(d.weekEnding || d.week);
        const mmdd = dateStr ? dateStr.substring(5) : '';
        svg.append('text')
          .attr('x', xPos).attr('y', height + 28)
          .attr('text-anchor', 'middle').style('font-size', '8px').style('fill', '#666')
          .text(mmdd);
      });

      // Y Axis
      svg.append('g')
        .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => countOrACVFormatterMillionFixedOne(d, true)))
        .selectAll('text').style('font-size', '9px');

      // Grid lines
      svg.append('g').attr('class', 'grid')
        .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
        .selectAll('line').style('stroke', '#e0e0e0').style('stroke-opacity', 0.7);
      svg.selectAll('.domain').remove();

      // Tooltip show/hide helpers
      const showTooltip = (d) => {
        const won = d.won || 0;
        const ml = d.mostLikely || 0;
        const bc = d.bestCase || 0;
        const forecast = won + ml;
        const forecastPlusBc = forecast + bc;
        const weekNum = d.week !== undefined ? d.week : d.weekLabel;
        const dateStr = formatWeekEnding(d.weekEnding || d.week);
        const mmdd = dateStr ? dateStr.substring(5) : '';

        const mouseX = d3.event.clientX;
        const mouseY = d3.event.clientY;

        tooltip.transition().duration(200).style('opacity', 1);
        tooltip
          .html(`
            <div style="border-top:10px solid #4471C4;border-radius:1rem;">
              <div style="padding:1.25rem;">
                <h3 style="text-align:center;font-weight:600;margin:0 0 0.5rem 0;">Week #${weekNum}: Ending ${mmdd} ($M)</h3>
                <hr style="border:none;border-top:1px solid #ddd;margin:0.5rem 0;">
                <div style="margin:5px;">
                <h4 style="text-align:left;font-weight:600;margin:0.3rem 0;">Forecast (W+ML+C): <span style="color:#4471C4">${fmtVal(forecast)}</span></h4>
                  <h4 style="text-align:left;font-weight:600;margin:0.3rem 0;">Forecast + B: <span style="color:#4471C4">${fmtVal(forecastPlusBc)}</span></h4>
                  <h4 style="text-align:left;font-weight:600;margin:0.3rem 0;">B: Best Case: <span style="color:#4471C4">${fmtVal(bc)}</span></h4>
                  <h4 style="text-align:left;font-weight:600;margin:0.3rem 0;">ML+C: Most Likely + Commit: <span style="color:#4471C4">${fmtVal(ml)}</span></h4>
                  <h4 style="text-align:left;font-weight:600;margin:0.3rem 0;">W: Won: <span style="color:#4471C4">${fmtVal(won)}</span></h4>
                </div>
              </div>
            </div>
          `)
          .style('left', (mouseX + 15) + 'px')
          .style('top', (mouseY - 28) + 'px');
      };

      const hideTooltip = () => {
        tooltip.transition().duration(300).style('opacity', 0);
      };

      // --- Draw bars ---

      // Won bars (bottom - green)
      svg.selectAll('.bar-won')
        .data(validData.filter(d => d.won > 0))
        .enter().append('rect').attr('class', 'bar-won')
        .attr('x', d => xScale(d.weekEnding || d.week))
        .attr('y', d => yScale(d.won || 0))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.won || 0))
        .attr('fill', '#70ad47')
        .style('cursor', 'pointer')
        .on('mouseover', function(d) { showTooltip(d); })
        .on('mouseout', hideTooltip)
        .on('click', function(d) {
          if (onDrill && d.weekEnding) onDrill({ type: 'won', quarter, weekEnding: d.weekEnding });
        });

      // Most Likely bars (middle - light green)
      svg.selectAll('.bar-likely')
        .data(validData.filter(d => d.mostLikely > 0))
        .enter().append('rect').attr('class', 'bar-likely')
        .attr('x', d => xScale(d.weekEnding || d.week))
        .attr('y', d => yScale((d.won || 0) + (d.mostLikely || 0)))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.mostLikely || 0))
        .attr('fill', '#c5e0b4')
        .style('cursor', 'pointer')
        .on('mouseover', function(d) { showTooltip(d); })
        .on('mouseout', hideTooltip)
        .on('click', function(d) {
          if (onDrill && d.weekEnding) onDrill({ type: 'mostLikely', quarter, weekEnding: d.weekEnding });
        });

      // Best Case bars (top - blue)
      svg.selectAll('.bar-bestcase')
        .data(validData.filter(d => (d.bestCase || 0) > 0))
        .enter().append('rect').attr('class', 'bar-bestcase')
        .attr('x', d => xScale(d.weekEnding || d.week))
        .attr('y', d => yScale((d.won || 0) + (d.mostLikely || 0) + (d.bestCase || 0)))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.bestCase || 0))
        .attr('fill', '#bdd7ee')
        .style('cursor', 'pointer')
        .on('mouseover', function(d) { showTooltip(d); })
        .on('mouseout', hideTooltip)
        .on('click', function(d) {
          if (onDrill && d.weekEnding) onDrill({ type: 'bestCase', quarter, weekEnding: d.weekEnding });
        });

      // --- Labels ---
      const minBarHeightForLabel = 15;
      const barWidth = xScale.bandwidth();
      const segmentFontSize = barWidth < 25 ? '6px' : '8px';
      const totalFontSize = barWidth < 25 ? '6px' : barWidth < 35 ? '7px' : '9px';

      // Won value labels
      svg.selectAll('.label-won')
        .data(validData.filter(d => d.won > 0 && (height - yScale(d.won || 0)) >= minBarHeightForLabel))
        .enter().append('text').attr('class', 'label-won')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => yScale(d.won || 0) + (height - yScale(d.won || 0)) / 2 + 4)
        .attr('text-anchor', 'middle').attr('fill', '#fff').attr('font-size', segmentFontSize).attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => fmtVal(d.won || 0));

      // Most Likely value labels
      svg.selectAll('.label-likely')
        .data(validData.filter(d => d.mostLikely > 0 && (height - yScale(d.mostLikely || 0)) >= minBarHeightForLabel))
        .enter().append('text').attr('class', 'label-likely')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => {
          const likelyHeight = height - yScale(d.mostLikely || 0);
          return yScale((d.won || 0) + (d.mostLikely || 0)) + likelyHeight / 2 + 4;
        })
        .attr('text-anchor', 'middle').attr('fill', '#4a7c31').attr('font-size', segmentFontSize).attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => fmtVal(d.mostLikely || 0));

      // Best Case value labels
      svg.selectAll('.label-bestcase')
        .data(validData.filter(d => (d.bestCase || 0) > 0 && (height - yScale(d.bestCase || 0)) >= minBarHeightForLabel))
        .enter().append('text').attr('class', 'label-bestcase')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => {
          const bcHeight = height - yScale(d.bestCase || 0);
          return yScale((d.won || 0) + (d.mostLikely || 0) + (d.bestCase || 0)) + bcHeight / 2 + 4;
        })
        .attr('text-anchor', 'middle').attr('fill', '#2e75b6').attr('font-size', segmentFontSize).attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => fmtVal(d.bestCase || 0));
      svg.selectAll('.label-total')
        .data(validData.filter(d => (d.won > 0 || d.mostLikely > 0 || (d.bestCase || 0) > 0)))
        .enter().append('text').attr('class', 'label-total')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => {
          const totalAll = (d.won || 0) + (d.mostLikely || 0) + (d.bestCase || 0);
          return yScale(totalAll) - 5;
        })
        .attr('text-anchor', 'middle').attr('fill', '#333').attr('font-size', totalFontSize).attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => {
          const total = (d.won || 0) + (d.mostLikely || 0);
          const totalAll = total + (d.bestCase || 0);
          return `${fmtVal(total)}/${fmtVal(totalAll)}`;
        });

      // Target line
      if (target > 0) {
        svg.append('line')
          .attr('x1', 0).attr('x2', width)
          .attr('y1', yScale(target)).attr('y2', yScale(target))
          .attr('stroke', '#bf9000').attr('stroke-width', 2);

        svg.append('text')
          .attr('x', 5).attr('y', yScale(target) - 5)
          .attr('fill', '#bf9000').attr('font-size', '10px').attr('font-weight', 'bold')
          .text(`Target: ${fmtVal(target)}`);
      }

      // Actual Won line (elapsed quarters only)
      if (showActualWon) {
        svg.append('line')
          .attr('x1', 0).attr('x2', width)
          .attr('y1', yScale(actualWon)).attr('y2', yScale(actualWon))
          .attr('stroke', '#385723').attr('stroke-width', 2);

        svg.append('text')
          .attr('x',5).attr('y', yScale(actualWon) - 5)
          .attr('fill', '#385723').attr('font-size', '10px').attr('font-weight', 'bold')
          .text(`Actual Won: ${fmtVal(actualWon)}`);
      }

      // Title
      let statusSuffix = '';
      if (status === 'elapsed') statusSuffix = ' (completed)';
      else if (status === 'current') statusSuffix = ' (current)';
      else if (status === 'future') statusSuffix = ' (future)';

      const titleText = config.title
        ? config.title.replace('{quarter}', quarterLabel) + statusSuffix
        : `ACV forecast for ${quarterLabel}${statusSuffix}`;
      const titleGroup = svg.append('g').attr('class', 'title-group');

      const titleTextEl = titleGroup.append('text')
        .attr('x', width / 2).attr('y', -10)
        .attr('text-anchor', 'middle').attr('font-size', '12px').attr('font-weight', 'bold')
        .text(titleText);

      if (status === 'current') {
        const bbox = titleTextEl.node().getBBox();
        const paddingX = 10;
        const paddingY = 4;
        titleGroup.insert('rect', 'text')
          .attr('x', bbox.x - paddingX).attr('y', bbox.y - paddingY)
          .attr('width', bbox.width + paddingX * 2).attr('height', bbox.height + paddingY * 2)
          .attr('fill', '#f5c518');
      }

      // X axis label
      svg.append('text')
        .attr('x', width / 2).attr('y', height + 50)
        .attr('text-anchor', 'middle').attr('font-size', '10px')
        .text('Week Ending');
    };

    draw();

    const ro = new ResizeObserver(() => draw());
    ro.observe(containerEl);

    return () => {
      ro.disconnect();
      if (tooltipRef.current) { tooltipRef.current.remove(); tooltipRef.current = null; }
    };
  }, [data, quarterLabel, quarter, status, target, actualWon, onDrill, config]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', height: '100%', position: 'relative' }} />;
};

export default ForecastTrendChart;
